// useTemplatePreview.ts
import { useEffect, useRef, useState } from "react"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { mergeTemplate } from "api/Engine/templates"
import { failureOf } from "utils/errMsgOf"
import { TemplateInputValue, TemplateRef } from "types/template.types"

/** How long the preview waits after the last change before it asks the server. */
export const PREVIEW_DEBOUNCE_MS = 400

export interface TemplatePreview {
  /** The last merged text (kept while a newer one loads) */
  text: string
  loading: boolean
  /** The server's message when the last merge failed */
  error: string | null
}

/**
 * The server's merge of a template with the current inputs, for the wizard's
 * preview. It waits 400 ms after the last change, and only the latest request
 * counts: an answer to an older one is dropped.
 *
 * @param ref - The project id and owner, and the template reference
 * @param values - The current inputs (string values)
 * @param exceptions - The ticked exceptions
 * @returns The merged text, whether a merge is under way, and the error
 */
export const useTemplatePreview = (
  ref: TemplateRef & { id: string; owner: string },
  values: TemplateInputValue[],
  exceptions: string[]
): TemplatePreview => {
  const getAxios = useAuthedAxios()
  const [preview, setPreview] = useState<TemplatePreview>({
    text: "",
    loading: true,
    error: null,
  })
  const latest = useRef(0)

  // One key for the whole request, so a new array with the same content
  // does not start another merge.
  const key = JSON.stringify([
    ref.id,
    ref.owner,
    ref.file,
    ref.access,
    ref.template,
    values,
    exceptions,
  ])

  useEffect(() => {
    const seq = ++latest.current
    setPreview((p) => ({ ...p, loading: true }))
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const [id, owner, file, access, template, inputs, excs] =
            JSON.parse(key) as [
            string,
            string,
            string,
            TemplateRef["access"],
            string,
            TemplateInputValue[],
            string[],
          ]
          const text = await mergeTemplate(await getAxios(), {
            id,
            owner,
            file,
            access,
            template,
            inputs,
            exceptions: excs,
          })
          if (seq === latest.current)
            setPreview({ text, loading: false, error: null })
        } catch (e) {
          if (seq === latest.current)
            setPreview((p) => ({ ...p, loading: false, error: failureOf(e) }))
        }
      })()
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [key, getAxios])

  return preview
}
